import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useBranding } from '../../context/BrandingContext';
import BrandMark from '../common/BrandMark';

// Inline SVG icons (stroke uses currentColor so they follow the nav text colour)
const svg = (paths) => (props) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {paths}
  </svg>
);

const Icons = {
  templates: svg(<>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="16" y2="17" />
  </>),
  trash: svg(<>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </>),
  submissions: svg(<>
    <path d="M9 11l3 3L22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </>),
  users: svg(<>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </>),
  settings: svg(<>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </>),
  links: svg(<>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </>),
  companies: svg(<>
    <path d="M3 21h18" />
    <path d="M5 21V7l7-4 7 4v14" />
    <path d="M9 21v-5h6v5" />
    <path d="M9 10h.01M15 10h.01M9 13h.01M15 13h.01" />
  </>),
};

// Navigation is derived from the caller's role, so a company admin sees its
// own company's tools and the platform owner sees the companies console.
const superadminNavSections = [
  {
    label: 'Platform',
    items: [
      { label: 'Companies', icon: Icons.companies, path: '/admin/companies' },
      { label: 'User support', icon: Icons.users, path: '/admin/users' },
    ],
  },
  {
    label: 'Account',
    items: [
      { label: 'Security', icon: Icons.settings, path: '/admin/change-password' },
    ],
  },
];

const companyAdminNavSections = [
  {
    label: 'Forms',
    items: [
      { label: 'Templates', icon: Icons.templates, path: '/admin/templates' },
      { label: 'Submissions', icon: Icons.submissions, path: '/admin/submissions' },
      { label: 'Trash', icon: Icons.trash, path: '/admin/trash' },
    ],
  },
  {
    label: 'Administration',
    items: [
      { label: 'Users', icon: Icons.users, path: '/admin/users' },
      { label: 'Company Profile', icon: Icons.companies, path: '/admin/company' },
      { label: 'My Account', icon: Icons.settings, path: '/admin/change-password' },
    ],
  },
];

const clinicNavItems = [
  { label: 'My Templates', icon: Icons.templates, path: '/portal/templates' },
  { label: 'My Links', icon: Icons.links, path: '/portal/links' },
  { label: 'My Submissions', icon: Icons.submissions, path: '/portal/submissions' },
];

const styles = {
  sidebar: {
    width: 250,
    minWidth: 250,
    height: '100vh',
    background: '#273947',
    display: 'flex',
    flexDirection: 'column',
    color: '#fff',
    flexShrink: 0,
    overflow: 'hidden',
  },
  header: {
    minHeight: 62,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '0 16px',
    background: 'rgba(0,0,0,0.16)',
    fontWeight: 'bold',
    fontSize: 18,
    letterSpacing: '0.5px',
  },
  sectionLabel: {
    padding: '16px 20px 6px',
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.9px',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.42)',
  },
    // The mark is a transparent PNG now, so it sits directly on the navy
    // chrome. The old white tile existed only to hide a background-less JPEG.
    logo: {
      height: 32,
      width: 'auto',
      objectFit: 'contain',
      flexShrink: 0,
    },
  brandText: {
    fontSize: 18,
    fontWeight: 700,
    color: '#fff',
    lineHeight: 1.3,
    letterSpacing: '0.5px',
  },
  nav: {
    flex: 1,
    padding: '20px 0',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    listStyle: 'none',
    margin: 0,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '9px 14px',
    borderRadius: 7,
    fontSize: 13.5,
    fontWeight: 500,
    color: 'rgba(255,255,255,0.78)',
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    width: 'calc(100% - 16px)',
    margin: '1px 8px',
    textAlign: 'left',
    transition: 'background 0.15s ease, color 0.15s ease',
    textDecoration: 'none',
  },
  navItemActive: {
    background: 'rgba(255,255,255,0.14)',
    color: '#fff',
    fontWeight: 600,
    boxShadow: 'inset 2px 0 0 #5bc0de',
  },
  navItemHover: {
    background: 'rgba(255,255,255,0.08)',
    color: '#fff',
  },
  navIcon: {
    width: 20,
    height: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: 'rgba(255,255,255,0.8)',
  },
  footer: {
    padding: '20px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    fontSize: 12,
    color: 'rgba(255,255,255,0.5)',
    textAlign: 'center',
  },
};

function getBrandLuminance(hex) {
  if (!/^#[\da-f]{6}$/i.test(hex || '')) return 0.1;
  const channels = [1, 3, 5].map((offset) => parseInt(hex.slice(offset, offset + 2), 16) / 255);
  const linear = channels.map((channel) => channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4);
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
}

export default function PortalSidebar() {
  const { user } = useAuth();
  const { branding } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();

  const isSuperadmin = user?.role === 'superadmin';
  const isCompanyAdmin = user?.role === 'companyadmin';
  const currentYear = new Date().getFullYear();
  const hasTenantTheme = branding.hasCustomPrimaryColor && /^#[\da-f]{6}$/i.test(branding.primaryColor || '');
  const lightBrand = hasTenantTheme && getBrandLuminance(branding.primaryColor) > 0.20;
  const sidebarText = lightBrand ? '#111827' : '#fff';
  const sidebarMuted = lightBrand ? 'rgba(17,24,39,0.62)' : 'rgba(255,255,255,0.65)';
  const sidebarStyles = hasTenantTheme ? {
    ...styles,
    sidebar: { ...styles.sidebar, background: branding.primaryColor, color: sidebarText },
    sectionLabel: { ...styles.sectionLabel, color: sidebarMuted },
    brandText: { ...styles.brandText, color: sidebarText },
    navItem: { ...styles.navItem, color: lightBrand ? 'rgba(17,24,39,0.82)' : 'rgba(255,255,255,0.86)' },
    navItemActive: {
      ...styles.navItemActive,
      background: lightBrand ? 'rgba(17,24,39,0.10)' : 'rgba(255,255,255,0.16)',
      color: sidebarText,
      boxShadow: `inset 3px 0 0 ${sidebarText}`,
    },
    navItemHover: {
      ...styles.navItemHover,
      background: lightBrand ? 'rgba(17,24,39,0.07)' : 'rgba(255,255,255,0.10)',
      color: sidebarText,
    },
    navIcon: { ...styles.navIcon, color: sidebarMuted },
    footer: { ...styles.footer, color: sidebarMuted, borderTopColor: lightBrand ? 'rgba(17,24,39,0.16)' : 'rgba(255,255,255,0.16)' },
  } : styles;

  const isActive = (itemPath) => {
    // Exact match or starts-with for nested routes (e.g., /admin/editor/xxx matches /admin/templates)
    if (location.pathname === itemPath) return true;
    // Special case: /admin/editor/* should highlight Templates
    if (itemPath === '/admin/templates' && location.pathname.startsWith('/admin/editor/')) return true;
    if (itemPath === '/admin/users' && location.pathname === '/admin/clinics') return true;
    if (itemPath === '/admin/company' && location.pathname === '/admin/company') return true;
    if (itemPath === '/admin/change-password' && (location.pathname === '/admin/security' || location.pathname === '/admin/change-password')) return true;
    return false;
  };

    const renderItem = (item) => {
    const active = isActive(item.path);
    const IconCmp = item.icon;
    return (
      <button
        key={item.path}
        style={{
          ...sidebarStyles.navItem,
          ...(active ? sidebarStyles.navItemActive : {}),
        }}
        onClick={() => navigate(item.path)}
        onMouseEnter={(e) => {
          if (!active) Object.assign(e.currentTarget.style, sidebarStyles.navItemHover);
        }}
        onMouseLeave={(e) => {
          if (!active) {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = sidebarStyles.navItem.color;
          }
        }}
      >
        <span style={sidebarStyles.navIcon}>{IconCmp && <IconCmp />}</span>
        {item.label}
      </button>
    );
  };

  const sections = isSuperadmin
    ? superadminNavSections
    : isCompanyAdmin
      ? companyAdminNavSections
      : null;

  return (
    <div style={sidebarStyles.sidebar}>
      <div style={{ ...sidebarStyles.header, color: sidebarText }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0, width: '100%' }}>
          {branding.hasCustomLogo && branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={branding.companyName}
              style={{ ...styles.logo, maxWidth: 205, maxHeight: 42, height: 'auto' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <>
              {branding.logoUrl
                ? <img src={branding.logoUrl} alt="" style={styles.logo} onError={(e) => { e.target.style.display = 'none'; }} />
                : <BrandMark light={!lightBrand} size={32} />}
              <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0 }}>
                <span style={sidebarStyles.brandText}>{branding.productName}</span>
                {branding.isCompanyBranded && (
                  <span style={{ fontSize: 11, color: sidebarMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {branding.companyName}
                  </span>
                )}
              </span>
            </>
          )}
        </span>
      </div>

      <nav style={sidebarStyles.nav}>
        {sections
          ? sections.map((section) => (
              <div key={section.label}>
                <div style={sidebarStyles.sectionLabel}>{section.label}</div>
                {section.items.map(renderItem)}
              </div>
            ))
          : clinicNavItems.map(renderItem)}
      </nav>

      <div style={sidebarStyles.footer}>
        © {currentYear} {branding.companyName}
      </div>
    </div>
  );
}
